import Phaser from 'phaser';
import { Suit, SUIT_SYMBOLS } from '@hokm/shared';

/** Configuration for symbol glow effect */
export interface SymbolGlowConfig {
  /** Font size for the main symbol in pixels */
  symbolSize: number;
  /** Main color of the symbol (hex number) */
  mainColor: number;
  /** Glow color (hex number) */
  glowColor: number;
  /** White stroke thickness (0 to disable) */
  strokeThickness?: number;
  /** Shadow offset for 3D effect (0 to disable) */
  shadowOffset?: number;
  /** Glow layer sizes relative to symbol size (default: [1.83, 1.58, 1.33]) */
  glowSizeMultipliers?: number[];
  /** Glow layer alphas (default: [0.1, 0.15, 0.22]) */
  glowAlphas?: number[];
}

/** Result of creating a symbol glow */
export interface SymbolGlowResult {
  container: Phaser.GameObjects.Container;
  glowLayers: Phaser.GameObjects.Text[];
  shadow: Phaser.GameObjects.Text | null;
  symbol: Phaser.GameObjects.Text;
  /** Set glow intensity multiplier (1 = normal, higher = brighter) */
  setGlowIntensity: (multiplier: number) => void;
  /** Set overall alpha for the entire symbol group */
  setAlpha: (alpha: number) => void;
}

/** Default glow configuration */
const DEFAULT_GLOW_SIZE_MULTIPLIERS = [1.83, 1.58, 1.33]; // 110/60, 95/60, 80/60
const DEFAULT_GLOW_ALPHAS = [0.1, 0.15, 0.22];

/**
 * Create a suit symbol with layered glow effect.
 * Used by TrumpSelector and Scoreboard for consistent visual style.
 */
export function createSymbolGlow(scene: Phaser.Scene, suit: Suit, config: SymbolGlowConfig): SymbolGlowResult {
  const {
    symbolSize,
    mainColor,
    glowColor,
    strokeThickness = 1.5,
    shadowOffset = 3,
    glowSizeMultipliers = DEFAULT_GLOW_SIZE_MULTIPLIERS,
    glowAlphas = DEFAULT_GLOW_ALPHAS,
  } = config;

  const container = scene.add.container(0, 0);
  const glowColorRgba = Phaser.Display.Color.IntegerToColor(glowColor).rgba;
  const mainColorRgba = Phaser.Display.Color.IntegerToColor(mainColor).rgba;
  const symbolText = SUIT_SYMBOLS[suit];

  // Create glow layers (outermost to innermost)
  const glowLayers: Phaser.GameObjects.Text[] = [];
  glowSizeMultipliers.forEach((multiplier, i) => {
    const size = Math.round(symbolSize * multiplier);
    const layer = scene.add
      .text(0, 0, symbolText, {
        fontFamily: 'Arial',
        fontSize: `${size}px`,
        color: glowColorRgba,
      })
      .setOrigin(0.5)
      .setAlpha(glowAlphas[i]);
    glowLayers.push(layer);
    container.add(layer);
  });

  // Shadow layer for 3D depth effect
  let shadow: Phaser.GameObjects.Text | null = null;
  if (shadowOffset > 0) {
    shadow = scene.add
      .text(shadowOffset, shadowOffset, symbolText, {
        fontFamily: 'Arial',
        fontSize: `${symbolSize}px`,
        color: '#000000',
      })
      .setOrigin(0.5)
      .setAlpha(0.4);
    container.add(shadow);
  }

  // Main symbol
  const symbolStyle: Phaser.Types.GameObjects.Text.TextStyle = {
    fontFamily: 'Arial',
    fontSize: `${symbolSize}px`,
    color: mainColorRgba,
  };
  if (strokeThickness > 0) {
    symbolStyle.stroke = '#ffffff';
    symbolStyle.strokeThickness = strokeThickness;
  }
  const symbol = scene.add.text(0, 0, symbolText, symbolStyle).setOrigin(0.5);
  container.add(symbol);

  // Helper to adjust glow intensity
  const setGlowIntensity = (multiplier: number) => {
    glowLayers.forEach((layer, i) => {
      layer.setAlpha(glowAlphas[i] * multiplier);
    });
  };

  // Helper to set overall alpha
  const setAlpha = (alpha: number) => {
    container.setAlpha(alpha);
  };

  return {
    container,
    glowLayers,
    shadow,
    symbol,
    setGlowIntensity,
    setAlpha,
  };
}

/** Suit color presets for consistent styling */
export const SUIT_COLORS: Record<Suit, { main: number; glow: number }> = {
  [Suit.SPADES]: { main: 0x2c3e50, glow: 0x5d6d7e },
  [Suit.HEARTS]: { main: 0xe74c3c, glow: 0xf1948a },
  [Suit.DIAMONDS]: { main: 0xe74c3c, glow: 0xf5b7b1 },
  [Suit.CLUBS]: { main: 0x2c3e50, glow: 0x85929e },
};
